import axios from '../core/http';

import { APIcaller, APIcallerv2 } from './APIcaller';

import { config } from '../config';

const baseApiUrl = `${config.baseApiUrl}/v1`;

export const getRecipesOfClient = ({
  computeRecipesCost = false,
  onlyAvailableInStocks = false,
  withTranslations = false,
  storeId = null,
  asOfDate = null,
} = {}) =>
  APIcallerv2.apiGet(`/recipes`, {
    computeRecipesCost,
    onlyAvailableInStocks,
    withTranslations,
    ...(storeId ? { storeId } : {}),
    ...(asOfDate ? { asOfDate } : {}),
  });

export const getRecipeParents = (id) => APIcallerv2.apiGet(`/recipes/${id}/parents-mappings`);

export const getRecipesByCategoriesForRecipeComposition = () =>
  APIcallerv2.apiGet(`/recipes/composition-choices`);

export const getRecipe = (recipeId, shouldFetchComposition = true) =>
  APIcallerv2.apiGet(`/recipes/${recipeId}`, { shouldFetchComposition });

export const getCompositions = (recipeId) =>
  APIcallerv2.apiGet(`/recipes/${recipeId}/compositions`);

export const getAssociatedRecipesByIds = (
  recipeIds,
  shouldFetchComposition = false,
  shouldFetchIngredientsRecursively = false,
  withTranslations = false,
) =>
  APIcaller.apiPost(`/admin/recipes/list`, {
    recipeIds,
    shouldFetchComposition,
    shouldFetchIngredientsRecursively,
    withTranslations,
  });

export const getRecipeCost = (recipeId, { withAllergens = true } = {}) =>
  APIcallerv2.apiGet(`/recipes/${recipeId}/cost`, { withAllergens });

export const create = (
  clientId,
  {
    img,
    recipePreparationSteps,
    name,
    unit,
    value,
    category,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    compositions,
    brandIds,
    inventoryListTemplates,
    categoryId,
    variants,
  },
) =>
  APIcaller.apiPost('/admin/recipes', {
    img,
    recipePreparationSteps,
    name,
    unit,
    value,
    clientId,
    category,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    compositions,
    brandIds,
    inventoryListTemplates,
    categoryId,
    variants,
  });

/**
 * Patches the metadata of a recipe only. The composition, the yield and the variants are owned by
 * updateComposition: sending them here would be silently dropped.
 */
export const patchById = ({
  id,
  img,
  recipePreparationSteps,
  name,
  unit,
  value,
  hasMultipleChannels,
  hasStock,
  hasLoss,
  hasTransfer,
  batchProduction,
  brandIds,
  inventoryListTemplates,
  categoryId,
}) =>
  APIcaller.apiPatch(`/admin/recipes/${id}`, {
    img,
    name,
    recipePreparationSteps,
    unit,
    value,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    brandIds,
    inventoryListTemplates,
    categoryId,
  });

/**
 * Sole writer of a recipe composition, versioned per channel on the API side. Only call it when the
 * composition actually changed: every call republishes the recipe calculations job.
 */
export const updateComposition = ({ id, value, compositions, variants, deletedVariantIds }) =>
  APIcallerv2.apiPatch(`/recipes/${id}/composition`, {
    value,
    compositions,
    variants,
    deletedVariantIds,
  });

export const disableRecipes = (recipeIds) =>
  APIcaller.apiPatch('/admin/recipes/disable', { recipeIds });

export const enableRecipe = (recipeId) =>
  axios.patch(
    baseApiUrl + '/admin/recipes/enable',
    {
      recipeIds: [recipeId],
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('loopbackAccessToken'),
      },
    },
  );

export const deleteDisabledRecipes = (recipeIds) =>
  axios.delete(baseApiUrl + '/admin/recipes', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem('loopbackAccessToken'),
    },
    data: {
      recipeIds,
    },
  });

export const checkForCircularRecipeMappings = (parentId, childId) =>
  APIcallerv2.apiGet('/recipes/check/circular-mappings', { parentId, childId });

export const activateMulitpleChannelsByIds = (entityIds) =>
  APIcaller.apiPost('/admin/recipes/activate-multiple-channels', {
    entityIds,
  });

export const downloadRecipeDetails = (
  clientId,
  recipeId,
  name,
  img,
  recipePreparationSteps,
  category,
  value,
  unit,
  compositions,
) =>
  APIcaller.apiPost('/admin/recipes/download-pdf', {
    clientId,
    recipeId,
    name,
    img,
    recipePreparationSteps,
    category,
    value,
    unit,
    compositions,
  });

export const updateRecipesAvailabilities = (entityIds, hasStock, hasLoss, hasTransfer) =>
  APIcaller.apiPatch('/admin/recipes/availability', {
    entityIds,
    hasStock,
    hasLoss,
    hasTransfer,
  });

export const patchCategories = (recipeIds, categoryId) =>
  APIcaller.apiPatch('/admin/recipes/categories', {
    recipeIds,
    categoryId,
  });

export const getRecipesByStoresTypes = (storeIds) =>
  APIcaller.apiPost('/admin/recipes/list/by-stores-types', { storeIds });

export const getVariantCompositions = (recipeIds) =>
  APIcallerv2.apiPost(`/recipes/variant-compositions`, { recipeIds });

export const getStoreVariantCompositions = (recipeIds, storeIds) =>
  APIcallerv2.apiPost(`/recipes/store-variant-compositions`, { recipeIds, storeIds });

export const getPaginated = ({ page, limit, search, orderBy, orderType }, customParams) =>
  APIcallerv2.apiPost(
    `/recipes/paginated?page=${page}&limit=${limit}&search=${search}&orderBy=${orderBy}&orderType=${orderType}`,
    { ...customParams },
  );

/**
 * Normalize a recipe composition payload (main recipe or variant) into the shape the cost
 * computation expects: the API returns the sales-channel mapping as a `channels` array, but the
 * consumers (Performances, cost utils) key off `channelIds`.
 */
export const formatRecipeComposition = (composition = []) =>
  composition.map(({ entity, channels = [], quantity, allergens = [] }) => ({
    entity,
    channelIds: channels.map(({ id }) => id),
    quantity,
    cost: entity.isIngredient ? entity.cost : null,
    allergens,
  }));

export const getRecipeVariants = async (recipeId) => {
  const { recipeVariants = [] } = await APIcallerv2.apiGet(`/recipes/${recipeId}/variants`, {
    withComposition: true,
  });

  return {
    recipeVariants: recipeVariants.map(({ id, name, stores = [], composition = [] }) => ({
      id,
      name,
      storeIds: stores.map((store) => store.id),
      composition: formatRecipeComposition(composition),
    })),
  };
};

export default {
  getVariantCompositions,
  getStoreVariantCompositions,
  getRecipesOfClient,
  getRecipeParents,
  getRecipe,
  getCompositions,
  getRecipeCost,
  create,
  patchById,
  updateComposition,
  disableRecipes,
  deleteDisabledRecipes,
  getAssociatedRecipesByIds,
  getRecipesByCategoriesForRecipeComposition,
  enableRecipe,
  checkForCircularRecipeMappings,
  activateMulitpleChannelsByIds,
  downloadRecipeDetails,
  updateRecipesAvailabilities,
  patchCategories,
  getRecipesByStoresTypes,
  getPaginated,
  getRecipeVariants,
  formatRecipeComposition,
};
